import type {
  AudienceIdentityMode,
  ParticipantIdentity,
  PublicAuthorDisplay,
  SessionPanelist,
} from "../../shared/types.ts";

/* Matches the projector splash's ROSTER_LIMIT: the welcome modal must stay a
   compact card, not a program page. */
const JOIN_WELCOME_SPEAKER_LIMIT = 8;

export interface JoinWelcomeIntro {
  title: string | null;
  speakers: SessionPanelist[];
}

export function joinWelcomeIntro(
  sessionTitle: string | null | undefined,
  panelists: SessionPanelist[],
): JoinWelcomeIntro {
  const title = sessionTitle?.trim() ?? "";
  return {
    title: title || null,
    speakers: panelists
      .filter((panelist) => panelist.name.trim())
      .slice(0, JOIN_WELCOME_SPEAKER_LIMIT),
  };
}

export interface ParticipantJoinPresentation {
  description: string;
  requiresAlias: boolean;
  fieldLabel: string | null;
  submitLabel: string;
}

export function participantJoinPresentation(
  mode: AudienceIdentityMode,
  publicAuthorDisplay: PublicAuthorDisplay,
): ParticipantJoinPresentation {
  if (mode === "fully_anonymous") {
    return {
      description: "We don’t collect your name. You’ll receive a private generated identity for this session.",
      requiresAlias: false,
      fieldLabel: null,
      submitLabel: "Join anonymously",
    };
  }
  const base = {
    requiresAlias: true,
    fieldLabel: "Your name or alias",
    submitLabel: "Join discussion",
  };
  if (publicAuthorDisplay === "hidden") {
    return {
      ...base,
      description: mode === "staff_identified"
        ? "Enter a name or alias to join. Your name will not be shown publicly — only moderators can see it."
        : "Enter a name or alias to join. Your name will not be shown publicly.",
    };
  }
  return {
    ...base,
    description: mode === "staff_identified"
      ? "Enter a name or alias to join. Other participants will see a generated identity, not your name. Moderators can see the name you enter."
      : "Enter a name or alias to join. Your name appears with your posts and replies.",
  };
}

export function composerIdentityCopy(
  identity: ParticipantIdentity,
  publicAuthorDisplay: PublicAuthorDisplay,
): string {
  if (publicAuthorDisplay === "hidden") {
    return identity.kind === "pseudonym"
      ? `Posting as ${identity.label} · No author label is shown publicly.`
      : `Posting as ${identity.label} · Your name will not be shown publicly.`;
  }
  if (identity.kind === "pseudonym") {
    return `Posting as ${identity.label} · Other participants see this generated identity.`;
  }
  return `Posting as ${identity.label} · Your name appears with your posts and replies.`;
}
